import {
  ArrowRightStartOnRectangleIcon,
  ArrowsPointingOutIcon,
  ArrowUturnLeftIcon,
  MicrophoneIcon,
  PhoneIcon,
  PhoneXMarkIcon,
  UserPlusIcon,
} from "@heroicons/react/24/outline";
import { t } from "i18next";
import { useEffect, useState } from "react";
import Draggable from "react-draggable";
import Button from "../Button";
import { useDispatch, useSelector } from "react-redux";
import { HashtagIcon, UserGroupIcon, UserIcon } from "@heroicons/react/24/solid";
import ConferenceAutoShrinkText from "./ConferenceAutoShrinkText";
import { TwilioState } from "../../state/twilio/slice";
import { AppDispatch } from "../../state/store";
import { fetchPhoneContact, fetchVoiceToken, finishTranferConference, setConferenceId, setTwilioDevice, setTwilioDeviceState } from "../../state/twilio/actions";
import { Call as TwilioCall, Device, TwilioError } from '@twilio/voice-sdk';
import { UserState } from "../../state/users/slice";
import { TransferCallModal } from "./TransferCallModal";
import { ConferenceModal } from "./ConferenceModal";
import { AgentsTwilioNumbers } from "../../models/User";
import { subscribeToCallEvents, notifyCallCreated } from "../../utils/callEvents";

interface Props {
}
export const Call = (props: Props) => {
  const twilio: TwilioState = useSelector((state: any) => state.twilio);
  const users: UserState = useSelector((state: any) => state.users);
  const dispatch = useDispatch<AppDispatch>();

  const [isCallShown, setIsCallShown] = useState(false);
  const [numberPadText, setNumberPadText] = useState("");
  const [showNumberPad, setShowNumberPad] = useState(false);
  const [currentCall, setCurrentCall] = useState<TwilioCall>();
  const [isRinging, setIsRinging] = useState(false);
  const [isCallMute, setIsCallMute] = useState(false);
  const [isRefreshingToken, setIsRefreshingToken] = useState(false);


  const [title, setTitle] = useState("Unknown Caller");
  const [callPhone, setCallPhone] = useState("");

  const [openTransferModal, setOpenTransferModal] = useState(false);
  const [openConferenceModal, setOpenConferenceModal] = useState(false);

  useEffect(() => {
    if (users.currentUser && !twilio.voiceToken) {
      dispatch(fetchVoiceToken());
    }
  }, [users.currentUser]);

  // Listen for call events from other components (like App.tsx)
  useEffect(() => {
    const unsubscribe = subscribeToCallEvents((event) => {
      if (event.type === 'call-created' && event.call) {
        //console.log('Call created from external source:', event.source);
        setCurrentCall(event.call);
        setIsCallShown(true);
        setIsRinging(false); // Outgoing calls don't ring
      } else if (event.type === 'call-ended') {
        console.log('Call ended from external source:', event.source);
        setCurrentCall(undefined);
        setIsCallShown(false);
        setIsRinging(false);
      }
    });

    // Cleanup subscription on unmount
    return unsubscribe;
  }, []);


  useEffect(() => {
    if (twilio.voiceToken) {
      if (twilio.device) {
        twilio.device.updateToken(twilio.voiceToken);
        return;
      } else {
        const device = new Device(twilio?.voiceToken, {
          // El evento 'TokenWillExpire' se emitirá 10 segundos antes de que el token de acceso caduque
          tokenRefreshMs: 10000,
        });
        dispatch(setTwilioDevice(device));
      }
    }

  }, [twilio.voiceToken])

  useEffect(() => {
    if (twilio.device) {
      console.log("Twilio device events are being set up");
      twilio.device.on(Device.EventName.Incoming, (incomingCall: TwilioCall) => {
        console.log("Device.EventName.Incoming");
        setIsRinging(true);
        setCurrentCall(incomingCall);
        setIsCallShown(true);
      });

      twilio.device.on(Device.EventName.Registered, (device) => {
        console.log("Twilio device registered");
        dispatch(setTwilioDeviceState(Device.State.Registered));
      });

      twilio.device.on(Device.EventName.Unregistered, (device) => {
        console.log("Twilio device unregistered");
        dispatch(setTwilioDeviceState(Device.State.Unregistered));
        dispatch(setTwilioDevice(undefined));
      });

      twilio.device.on(Device.EventName.Error, async (error: any, call?: TwilioCall) => {
        console.error("Twilio Device Error:", error);
        console.log("Código de error", error.code);
      });

      twilio.device.on(Device.EventName.TokenWillExpire, () => {
        if (!isRefreshingToken) {
          console.log("Twilio device token will expire soon");
          setIsRefreshingToken(true);
          dispatch(fetchVoiceToken()).finally(() => {
            setIsRefreshingToken(false);
          });
        }
      });

      if (twilio.device.state !== Device.State.Registered) {
        twilio.device.register();
      }

    }
  }, [twilio.device])

  useEffect(() => {
    console.log("Current call changed:", currentCall);
    if (!currentCall) { return; }

    currentCall.on("accept", (call: TwilioCall) => {
      console.log('The call was accepted.');
      setIsRinging(false);
    });
    currentCall.on("disconnect", (call: TwilioCall) => {
      console.log('The call has been disconnected.');
      cleanDevice();
    });
    currentCall.on("cancel", () => {
      console.log('The call has been canceled.');
      cleanDevice();
    });
    currentCall.on("error", (error) => {
      console.error('An error occurred during the call:', error);
    });

    let phone: string | undefined = undefined
    if (currentCall?.direction && currentCall.direction === TwilioCall.CallDirection.Incoming) {
      phone = currentCall?.parameters.From
    } else if (currentCall?.direction && currentCall.direction === TwilioCall.CallDirection.Outgoing) {
      phone = currentCall?.customParameters.get("To")
    }

    if (phone) {
      console.log("Phone number detected:", phone);
      setTitle("Searching contact...")
      setCallPhone(phone)

    } else {
      console.log("No phone number detected");
      setTitle("Unknown Caller")
      setCallPhone("")
    }
  }, [currentCall])

  useEffect(()=>{
    if (currentCall === undefined && twilio.transferConferenceId && twilio.transferTo) {
      const params = { To: twilio.transferTo! };
      twilio.device?.connect({params: params}).
      then((outgoingCall: any) => {
          notifyCallCreated(outgoingCall, 'Call transfer - callNext');
      }).catch((error: any) => {
          console.error('Failed to create outgoing call:', error);
      });
      toggleTransferModal()
    }
  }, [currentCall, twilio.transferConferenceId, twilio.transferTo])

  useEffect(() => {
    if (!callPhone) { return; }
    console.log("Fetching contact for phone:", callPhone);
    dispatch(fetchPhoneContact(callPhone))
  }, [callPhone])

  useEffect(() => {
    if (twilio.phoneContact === null) { return; }
    const newMap = new Map();
    const phoneContact = twilio?.phoneContact; // { type: "client" | "lead" | null, contact: [...] }
    if (phoneContact?.contact?.length) {
      const label =
        phoneContact.type === "client" ? "Client" :
          phoneContact.type === "lead" ? "Lead" : "";

      phoneContact.contact.forEach((element: any) => {
        newMap.set(element.phone, {
          ...element,
          type: label,
        });
      });
    }

    users?.agentsTwilioNumbers?.forEach((element: AgentsTwilioNumbers) => {
      newMap.set(element.twilio_phone, {
        ...element,
        name: `${element.first_name} ${element.last_name}`,
        type: "Agent"
      });
    });
    
    if (newMap.get(callPhone)) {
      const { name, type, phone } = newMap.get(callPhone);
      if (name && name !== "") {
        setTitle(`${name} - ${type}`);
      } else {
        setTitle("Unknown Caller");
      }
    } else {
      setTitle("Unknown Caller");
    }
    //const { name, type, phone } = newMap.get(callPhone);

  }, [twilio.phoneContact, users?.agentsTwilioNumbers])

  useEffect(() => {
    if (!showNumberPad) {
      setNumberPadText("");
    }
  }, [showNumberPad])

  const cleanDevice = () => {
    setIsRinging(false);
    setCallPhone("")
    setCurrentCall(undefined);
    setIsCallShown(false);
    if(twilio.conferenceId) {
      dispatch(setConferenceId(undefined))
    }
  }

  const acceptCall = () => {
    currentCall?.accept();
  };

  const endCall = () => {
    currentCall?.reject();
    twilio.device?.disconnectAll();
    cleanDevice()
  };

  const muteCall = () => {
    //twilio.device?.connect({ params: { To: "+18132866564" } });
    setIsCallMute(true);
    currentCall?.mute(true);
  };

  const unmuteCall = () => {
    setIsCallMute(false);
    currentCall?.mute(false);
  };

  const sendDigits = (digit: string) => {
    setNumberPadText(numberPadText + digit);
    currentCall?.sendDigits(digit);
  };

  const toggleTransferModal = () => {
    setOpenTransferModal(!openTransferModal);
  };

  const transferFinalStep = (accepted: boolean) => {
    dispatch(
      finishTranferConference({
        call_sid: currentCall?.parameters.CallSid ?? "",
        client_conference_id: twilio.transferConferenceId ?? "",
        transfer_was_accepted: accepted,
      })
    );
  };

  const toggleConferenceModal = () => {
    setOpenConferenceModal(!openConferenceModal);
  };

  return (<div>
    {isCallShown &&
      <Draggable handle="strong">
        <div className="absolute z-40 top-[10%] left-1/2 rounded-lg w-fit h-fit p-4 text-white min-w-[250px] bg-blue shadow-lg max-w-[500px]">

          <div className="flex justify-between">
            <div>
            </div>
            <strong className="cursor">
              <ArrowsPointingOutIcon className="w-5 h-5 cursor-pointer z-40" />
            </strong>
          </div>
          <div className="flex flex-col justify-center items=center mb-4">
            {twilio.conferenceId && <p className="opacity-40 text-lg text-center -mt-1">{t("CONFERENCE")}</p>}
            <p className="text-xl font-semibold text-center w-80">{title}</p>
            <p className="opacity-40 text-base text-center -mt-1">{callPhone}</p>
            {!showNumberPad && <div> 
              {!twilio.conferenceId && <UserIcon className="size-24 cursor-pointer mx-auto my-4" /> }
              {twilio.conferenceId && <UserGroupIcon className="size-24 cursor-pointer mx-auto my-4"/>}
            </div>}
            {showNumberPad && (
              <div className="w-full text-center font-light text-4xl px-4 py-2 max-w-80">
                <ConferenceAutoShrinkText
                  children={numberPadText.split("").join(" ")}
                />
              </div>
            )}
            {showNumberPad &&
              <div className="grid-cols-3 grid gap-5 py-5 px-8">
                {["1", "2", "3", "4", "5", "6", "7", "8", "9", "*", "0", "#"].map(
                  (ele) => (
                    <div className="flex item-center justify-center">
                      <Button
                        tooltipClassName="bg-gray top-10 h-fit left-[-2rem]"
                        onClick={() => sendDigits(ele)}
                        isSecondary
                        iconOnly
                        classNames="bg-neutral-200 hover:bg-opacity-40 bg-opacity-20"
                      >
                        <div className="w-8 h-8 flex item-center justify-center text-2xl">
                          {ele}
                        </div>
                      </Button>
                    </div>
                  )
                )}
              </div>
            }
            <div className="flex justify-center mb-4 gap-4">
              {!isRinging && isCallMute &&
                <Button
                  onClick={unmuteCall}
                  isSecondary
                  iconOnly
                  classNames="bg-neutral-200 bg-opacity-40 hover:bg-opacity-20"
                >
                  <div className="relative flex justify-center items-center">
                    <MicrophoneIcon className="w-7 h-7 cursor-pointer z-40 m-2" />
                    <div className="bg-neutral-200 h-2 aspect-square absolute transform rotate-45 h-6 w-[1px]"></div>
                  </div>
                </Button>}

              {!isRinging && !isCallMute &&
                <Button
                  onClick={muteCall}
                  isSecondary
                  iconOnly
                  classNames="bg-neutral-200 hover:bg-opacity-40 bg-opacity-20"
                >
                  <MicrophoneIcon className="w-7 h-7 cursor-pointer z-40 m-2" />
                </Button>}


              <Button
                onClick={endCall}
                isSecondary
                iconOnly
                classNames="bg-red-600 bg-opacity-100 hover:bg-opacity-80 hover:bg-red-600"
              >
                <PhoneXMarkIcon className="w-7 h-7 cursor-pointer z-40 m-2" />
              </Button>
              {isRinging && <Button
                onClick={acceptCall}
                isSecondary
                iconOnly
                classNames="bg-green-600 bg-opacity-100 hover:bg-opacity-80 hover:bg-green-600"
              >
                <PhoneIcon className="w-7 h-7 cursor-pointer z-40 m-2" />
              </Button>}
            </div>

            {!isRinging &&
              <div className="flex flex-col gap-4">
                {twilio.transferConferenceId && <Button
                  onClick={() => transferFinalStep(false)}
                  isSecondary
                  classNames="flex justify-center w-full items-center gap-2 py-2 bg-neutral-200 hover:bg-opacity-40 bg-opacity-20"
                >
                  <ArrowUturnLeftIcon className="w-6 h-6 cursor-pointer z-40" />
                  <div className="">{t("BACK_TO_CLIENT")}</div>
                </Button>}

                {twilio.transferConferenceId && <Button
                  onClick={() => transferFinalStep(true)}
                  isSecondary
                  classNames="flex justify-center w-full items-center gap-2 py-2 bg-neutral-200 hover:bg-opacity-40 bg-opacity-20"
                >
                  <ArrowRightStartOnRectangleIcon className="w-6 h-6 cursor-pointer z-40" />
                  <div>{t("TRANSFER_CALL")}</div>
                </Button>}

                {!twilio.transferConferenceId && !twilio.conferenceId && <Button
                  onClick={toggleTransferModal}
                  isSecondary
                  classNames="flex justify-center w-full items-center gap-2 py-2 bg-neutral-200 hover:bg-opacity-40 bg-opacity-20"
                >
                  <ArrowRightStartOnRectangleIcon className="w-6 h-6 cursor-pointer z-40" />
                  <div>{t("TRANSFER_CALL")}</div>
                </Button>}

                {!twilio.transferConferenceId && <Button
                  onClick={toggleConferenceModal}
                  isSecondary
                  classNames="flex justify-center w-full items-center gap-2 py-2 bg-neutral-200 hover:bg-opacity-40 bg-opacity-20"
                >
                  <UserPlusIcon className="w-6 h-6 cursor-pointer z-40" />
                  <div>{t("CONFERENCE")}</div>
                </Button>}

                <Button
                  onClick={() => setShowNumberPad((value) => !value)}
                  isSecondary
                  classNames="flex justify-center w-full items-center gap-2 py-2 bg-neutral-200 hover:bg-opacity-40 bg-opacity-20"
                >
                  <HashtagIcon className="w-6 h-6 cursor-pointer z-40" />
                  <div>{t("NUMBER_PAD")}</div>
                </Button>


              </div>}
          </div>

        </div>
      </Draggable>
    }
    <TransferCallModal
      isOpen={openTransferModal}
      onClose={toggleTransferModal}
      callSid={currentCall?.parameters?.CallSid ?? ""}
    />
    <ConferenceModal
      isOpen={openConferenceModal}
      onClose={toggleConferenceModal}
      callSid={currentCall?.parameters?.CallSid ?? ""}
    />
  </div>
  );
};
